import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button, Card, Screen, Text } from "@andrum/ui";
import { useAuthStore } from "@/stores/auth-store";
import {
  acceptPracticeInvite,
  fetchConsentMeta,
  peekInvite,
  type InvitePeek,
  type PracticeConsentMeta,
} from "@/features/practice/api/practice-api";

export function JoinScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { token?: string };
  const token = params.token;
  const { status } = useAuthStore();

  const [peek, setPeek] = useState<InvitePeek | null>(null);
  const [meta, setMeta] = useState<PracticeConsentMeta | null>(null);
  const [code, setCode] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    void fetchConsentMeta().then(setMeta);
    if (token) {
      void peekInvite({ token }).then(setPeek);
    }
  }, [token]);

  const handleAccept = async () => {
    setError(null);
    if (!consent) {
      setError(t("practice.mustConsent"));
      return;
    }
    if (status !== "authenticated") {
      // Stash and send to login
      try {
        if (token) sessionStorage.setItem("andrum.practice_invite_token", token);
        if (code) sessionStorage.setItem("andrum.practice_invite_code", code);
      } catch {
        // ignore
      }
      void navigate({ to: "/login" });
      return;
    }
    setBusy(true);
    const result = await acceptPracticeInvite(
      token ? { token } : { code: code.trim() }
    );
    setBusy(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setDone(result.practiceName);
    try {
      sessionStorage.removeItem("andrum.practice_invite_token");
      sessionStorage.removeItem("andrum.practice_invite_code");
    } catch {
      // ignore
    }
  };

  // Auto-resume after login
  useEffect(() => {
    if (status !== "authenticated" || token) return;
    try {
      const stashed = sessionStorage.getItem("andrum.practice_invite_token");
      const stashedCode = sessionStorage.getItem("andrum.practice_invite_code");
      if (stashed) {
        void navigate({ to: "/join/$token", params: { token: stashed } });
      } else if (stashedCode) {
        setCode(stashedCode);
      }
    } catch {
      // ignore
    }
  }, [status, token, navigate]);

  if (done) {
    return (
      <Screen className="gap-4 py-8">
        <Text variant="title">{t("practice.connectedTitle")}</Text>
        <Text variant="body" className="text-text-secondary">
          {t("practice.connectedBody", { name: done })}
        </Text>
        <Button onClick={() => void navigate({ to: "/app/today" })}>
          {t("practice.goToday")}
        </Button>
      </Screen>
    );
  }

  return (
    <Screen className="gap-4 py-6">
      <Text variant="label" className="text-primary">
        andrum Practice
      </Text>
      <Text variant="title">{t("practice.joinTitle")}</Text>
      <Text variant="body" className="text-text-secondary">
        {peek?.clinicianName
          ? t("practice.joinFrom", {
              clinician: peek.clinicianName,
              practice: peek.practiceName ?? "",
            })
          : t("practice.joinBody")}
      </Text>

      {peek && !peek.valid ? (
        <Card className="border-danger/30 bg-danger/5 p-4">
          <Text variant="body">{t("practice.inviteInvalid")}</Text>
        </Card>
      ) : null}

      {!token ? (
        <Card className="gap-2 p-4">
          <Text variant="label">{t("practice.enterCode")}</Text>
          <input
            className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-base"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ABCD1234"
            autoCapitalize="characters"
          />
        </Card>
      ) : null}

      <Card className="gap-3 p-4">
        <Text variant="heading">{t("practice.consentTitle")}</Text>
        <ul className="list-disc space-y-2 pl-5 text-sm text-text-secondary">
          {(meta?.summarySv ?? []).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <label className="mt-2 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1"
          />
          <span>{t("practice.consentCheck")}</span>
        </label>
      </Card>

      {error ? (
        <Text variant="body" className="text-sm text-danger">
          {error}
        </Text>
      ) : null}

      <Button
        disabled={busy || (peek ? !peek.valid : false) || (!token && code.length < 4)}
        onClick={() => void handleAccept()}
      >
        {status === "authenticated"
          ? t("practice.accept")
          : t("practice.loginToAccept")}
      </Button>

      <Link to="/welcome" className="text-center text-sm text-text-secondary">
        {t("back")}
      </Link>
    </Screen>
  );
}
